import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from '../../axios/axiosconf';
import ModalCIE10 from './ModalCIE10';
import SearchableSelect from './SearchableSelect';

const CUPS_RIPS_MODAL_CHIP_CSS = `
.cups-rips-modal-portal .chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
.cups-rips-modal-portal .chip-filter { border: 1px solid rgba(23,162,184,0.25); background: rgba(23,162,184,0.06); color: #17a2b8; border-radius: 999px; padding: 5px 12px; font-size: 0.75rem; font-weight: 700; cursor: pointer; }
.cups-rips-modal-portal .chip-filter.active { background: #17a2b8; color: #fff; border-color: #17a2b8; }
`;

function RegistroRipsConvenio({ paciente }) {
    const [formData, setFormData] = useState({
        paciente_id: '',
        nombre_paciente: '',
        tipo: 'CC',
        ndocumento: '',
        fecha: '',
        incapacidad: 'NO',
        tipo_usuario: '12',
        profesionalcode: '',
        tipo_consulta: false,
        tipo_procedimiento: false,
        modalidad_consulta: '01',
        grupo_servicios: '01',
        servicios: '',
        finalidadconsulta: '',
        causaexternaconsulta: '',
        modalidad_procedimiento: '01',
        grupo_servicios_procedimientos: '01',
        servicios_procedimientos: '',
        finalidadprocedimiento: '',
        conceptoRecaudo: '05',
        valorpagomoderador: '0',
        cupsc: '',
        cupsp: '',
        cie10c: '',
        cie10c1: '',
        cie10c2: '',
        cie10c3: '',
        cie10p: '',
        cie10p1: '',
        cie10pc: '',
        listaprecios_consulta_id: '',
        valor_consulta: '0',
        listaprecios_procedimiento_id: '',
        valor_procedimiento: '0'
    });

    // Llenar datos del paciente cuando se reciben las props
    React.useEffect(() => {
        if (paciente) {
            const nombreCompleto = `${paciente.primerNombre || ''} ${paciente.segundoNombre || ''} ${paciente.primerApellido || ''} ${paciente.segundoApellido || ''}`.trim();
            setFormData(prev => ({
                ...prev,
                paciente_id: paciente.id || '',
                nombre_paciente: nombreCompleto,
                tipo: paciente.tipoDocumento || 'CC',
                ndocumento: paciente.numeroDocumento || '',
                fecha: new Date().toISOString().split('T')[0]
            }));
        }
    }, [paciente]);

    // Cargar datos de API
    React.useEffect(() => {
        let userdata = JSON.parse(localStorage.getItem('userdata'));
        const config = {
            headers: { Authorization: 'Bearer ' + userdata.access_token }
        };

        // Cargar profesionales
        axios.get('/usuarios?uid=' + userdata.user_id, config)
            .then(response => {
                if (response.status === 200) {
                    setProfesionales(response.data);
                }
            })
            .catch(error => console.log('Error cargando profesionales:', error));

        // Cargar Servicios desde API
        axios.get('/rips-servicios?uid=' + userdata.user_id, config)
            .then(response => {
                if (response.status === 200) {
                    setServicios(response.data);
                }
            })
            .catch(error => console.log('Error cargando servicios:', error));
        
        // Cargar Finalidad Consulta desde API
        axios.get('/rips-finalidad-consulta?uid=' + userdata.user_id, config)
            .then(response => {
                if (response.status === 200) {
                    setFinalidadConsulta(response.data);
                    setFinalidadProcedimiento(response.data); // Usar el mismo para procedimiento
                }
            })
            .catch(error => console.log('Error cargando finalidad consulta:', error));
        
        // Cargar Causa Externa desde API
        axios.get('/rips-causa-externa?uid=' + userdata.user_id, config)
            .then(response => {
                if (response.status === 200) {
                    setCausaExterna(response.data);
                }
            })
            .catch(error => console.log('Error cargando causa externa:', error));
    }, []);

    const [showConsulta, setShowConsulta] = useState(false);
    const [showProcedimiento, setShowProcedimiento] = useState(false);

    // Estados para modales
    const [showModalConsulta, setShowModalConsulta] = useState(false);
    const [showModalProcedimiento, setShowModalProcedimiento] = useState(false);
    const [listaConsultas, setListaConsultas] = useState([]);
    const [listaProcedimientos, setListaProcedimientos] = useState([]);
    const [filtroCupsModal, setFiltroCupsModal] = useState('');
    const [clasificacionFiltroCupsModal, setClasificacionFiltroCupsModal] = useState('');

    const clasificacionesConsultaModal = useMemo(() => {
        return Array.from(
            new Set(listaConsultas.map((item) => String(item.clasificacion || '').trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
    }, [listaConsultas]);

    const clasificacionesProcedimientoModal = useMemo(() => {
        return Array.from(
            new Set(listaProcedimientos.map((item) => String(item.clasificacion || '').trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
    }, [listaProcedimientos]);

    const consultasCupsFiltradas = useMemo(() => {
        let list = listaConsultas;
        if (clasificacionFiltroCupsModal) {
            list = list.filter((item) => String(item.clasificacion ?? '').trim() === clasificacionFiltroCupsModal);
        }
        const t = filtroCupsModal.trim().toLowerCase();
        if (!t) return list;
        return list.filter((item) => {
            const cod = String(item.codigorips ?? '').toLowerCase();
            const nom = String(item.nombre ?? '').toLowerCase();
            return cod.includes(t) || nom.includes(t);
        });
    }, [listaConsultas, filtroCupsModal, clasificacionFiltroCupsModal]);

    const procedimientosCupsFiltrados = useMemo(() => {
        let list = listaProcedimientos;
        if (clasificacionFiltroCupsModal) {
            list = list.filter((item) => String(item.clasificacion ?? '').trim() === clasificacionFiltroCupsModal);
        }
        const t = filtroCupsModal.trim().toLowerCase();
        if (!t) return list;
        return list.filter((item) => {
            const cod = String(item.codigorips ?? '').toLowerCase();
            const nom = String(item.nombre ?? '').toLowerCase();
            return cod.includes(t) || nom.includes(t);
        });
    }, [listaProcedimientos, filtroCupsModal, clasificacionFiltroCupsModal]);

    // Estados para modales CIE10
    const [showModalCIE10, setShowModalCIE10] = useState(false);
    const [cie10Field, setCie10Field] = useState('');
    const [camposFaltantes, setCamposFaltantes] = useState({});

    // Estados para datos de API
    const [profesionales, setProfesionales] = useState([]);

    // Estados para JSON de RIPS
    const [servicios, setServicios] = useState([]);
    const [finalidadConsulta, setFinalidadConsulta] = useState([]);
    const [finalidadProcedimiento, setFinalidadProcedimiento] = useState([]);
    const [causaExterna, setCausaExterna] = useState([]);


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newFormData = {
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        };


        setFormData(newFormData);
        setCamposFaltantes(prev => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });

        if (name === 'tipo_consulta') {
            setShowConsulta(checked);
        } else if (name === 'tipo_procedimiento') {
            setShowProcedimiento(checked);
        }
    };

    const campoConError = (campo) => Boolean(camposFaltantes[campo]);



    const cargarConsultas = async () => {
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const config = {
                headers: { Authorization: 'Bearer ' + userdata.access_token },
                params: { uid: userdata.user_id, clasificacionrips: 1 }
            };
            const response = await axios.get('/lista-precio-rips', config);
            setListaConsultas(response.data);
        } catch (error) {
            console.error('Error cargando consultas:', error);
        }
    };

    const cargarProcedimientos = async () => {
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const config = {
                headers: { Authorization: 'Bearer ' + userdata.access_token },
                params: { uid: userdata.user_id, clasificacionrips: 2 }
            };
            const response = await axios.get('/lista-precio-rips', config);
            setListaProcedimientos(response.data);
        } catch (error) {
            console.error('Error cargando procedimientos:', error);
        }
    };

    const abrirModalConsulta = () => {
        setFiltroCupsModal('');
        setClasificacionFiltroCupsModal('');
        cargarConsultas();
        setShowModalConsulta(true);
    };

    const abrirModalProcedimiento = () => {
        setFiltroCupsModal('');
        setClasificacionFiltroCupsModal('');
        cargarProcedimientos();
        setShowModalProcedimiento(true);
    };

    const seleccionarConsulta = (item) => {
        setFormData(prev => ({ 
            ...prev, 
            cupsc: item.codigorips,
            listaprecios_consulta_id: item.id,
            valor_consulta: item.precio || '0'
        }));
        setShowModalConsulta(false);
    };

    const seleccionarProcedimiento = (item) => {
        setFormData(prev => ({ 
            ...prev, 
            cupsp: item.codigorips,
            listaprecios_procedimiento_id: item.id,
            valor_procedimiento: item.precio || '0'
        }));
        setShowModalProcedimiento(false);
    };

    const abrirModalCIE10 = (fieldName) => {
        setCie10Field(fieldName);
        setShowModalCIE10(true);
    };

    const seleccionarCIE10 = (codigo) => {
        setFormData(prev => ({ ...prev, [cie10Field]: codigo }));
        setShowModalCIE10(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const hayDiagnosticosDuplicados = (campos) => {
            const vistos = new Set();
            for (const campo of campos) {
                const valor = String(formData[campo] || '').trim().toUpperCase();
                if (!valor) continue;
                if (vistos.has(valor)) return true;
                vistos.add(valor);
            }
            return false;
        };

        // Validaciones básicas
        if (!formData.tipo_consulta && !formData.tipo_procedimiento) {
            alert('Debe seleccionar al menos Consulta o Procedimiento');
            return;
        }

        if (!formData.profesionalcode) {
            alert('Por favor seleccione el profesional');
            return;
        }

        const nuevosCamposFaltantes = {};
        const marcarSiVacio = (campo) => {
            if (!String(formData[campo] ?? '').trim()) {
                nuevosCamposFaltantes[campo] = true;
            }
        };

        // Validar Consulta
        if (formData.tipo_consulta) {
            ['cupsc', 'cie10c', 'modalidad_consulta', 'grupo_servicios', 'servicios', 'finalidadconsulta', 'causaexternaconsulta'].forEach(marcarSiVacio);
        }

        // Validar Procedimiento
        if (formData.tipo_procedimiento) {
            ['cupsp', 'cie10p', 'modalidad_procedimiento', 'grupo_servicios_procedimientos', 'servicios_procedimientos', 'finalidadprocedimiento'].forEach(marcarSiVacio);
        }

        setCamposFaltantes(nuevosCamposFaltantes);

        if (Object.keys(nuevosCamposFaltantes).length > 0) {
            if (formData.tipo_consulta && Object.keys(nuevosCamposFaltantes).some(k => ['cupsc', 'cie10c', 'modalidad_consulta', 'grupo_servicios', 'servicios', 'finalidadconsulta', 'causaexternaconsulta'].includes(k))) {
                alert('Para Consulta, complete: Código CUPS, Diagnóstico Principal, Modalidad, Grupo de Servicios, Servicio, Finalidad y Causa Externa.');
                return;
            }
            if (formData.tipo_procedimiento) {
                alert('Para Procedimiento, complete: Código CUPS, Diagnóstico Principal, Modalidad, Grupo de Servicios, Servicio y Finalidad.');
                return;
            }
        }

        if (formData.tipo_consulta) {
            if (hayDiagnosticosDuplicados(['cie10c', 'cie10c1', 'cie10c2', 'cie10c3'])) {
                alert('En Consulta, los diagnósticos CIE10 no pueden repetirse entre principal y relacionados.');
                return;
            }
        }

        if (formData.tipo_procedimiento) {
            if (hayDiagnosticosDuplicados(['cie10p', 'cie10p1', 'cie10pc'])) {
                alert('En Procedimiento, los diagnósticos CIE10 no pueden repetirse entre principal y relacionados.');
                return;
            }
        }

        let userdata = JSON.parse(localStorage.getItem('userdata'));
        const config = {
            headers: {
                Authorization: 'Bearer ' + userdata.access_token,
                'Content-Type': 'application/json'
            }
        };

        const ripsData = {
            ...formData,
            uid: userdata.user_id
        };

        try {
            const response = await axios.post('/rips-convenio', ripsData, config);
            if (response.status === 200 || response.status === 201) {
                alert('RIPS por convenio guardado exitosamente. Estado: Pendiente de facturación');
                
                // Limpiar formulario completamente
                setFormData({
                    paciente_id: '',
                    nombre_paciente: '',
                    tipo: 'CC',
                    ndocumento: '',
                    fecha: new Date().toISOString().split('T')[0],
                    incapacidad: 'NO',
                    tipo_usuario: '12',
                    profesionalcode: '',
                    tipo_consulta: false,
                    tipo_procedimiento: false,
                    modalidad_consulta: '01',
                    grupo_servicios: '01',
                    servicios: '',
                    finalidadconsulta: '',
                    causaexternaconsulta: '',
                    modalidad_procedimiento: '01',
                    grupo_servicios_procedimientos: '01',
                    servicios_procedimientos: '',
                    finalidadprocedimiento: '',
                    conceptoRecaudo: '05',
                    valorpagomoderador: '0',
                    cupsc: '',
                    cupsp: '',
                    cie10c: '',
                    cie10c1: '',
                    cie10c2: '',
                    cie10c3: '',
                    cie10p: '',
                    cie10p1: '',
                    cie10pc: '',
                    listaprecios_consulta_id: '',
                    valor_consulta: '0',
                    listaprecios_procedimiento_id: '',
                    valor_procedimiento: '0'
                });
                
                // Ocultar secciones de consulta y procedimiento
                setShowConsulta(false);
                setShowProcedimiento(false);
            }
        } catch (error) {
            console.error('Error al guardar:', error);
            alert('Error al guardar RIPS: ' + (error.response?.data?.message || error.message));
        }
    };

    const pacienteYaCargado = Boolean(
        paciente ||
        String(formData.paciente_id || '').trim() !== ''
    );

    return (
        <div className="container-fluid">
            <form onSubmit={handleSubmit}>
                <div className="card card-info card-outline">
                    <div className="card-header"><b>Información del Paciente</b></div>
                    <div className="card-body">
                        {!pacienteYaCargado && (
                            <div className="form-group">
                                <button type="button" className="btn btn-info">
                                    <i className="fas fa-edit"></i>Seleccionar Paciente
                                </button>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label>Nombre del Paciente *</label>
                                    <input type="text" name="nombre_paciente" className="form-control text-uppercase"
                                        value={formData.nombre_paciente} readOnly />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Tipo *</label>
                                    <select name="tipo" className="form-control" value={formData.tipo} onChange={handleInputChange} required>
                                        <option value="CC">CC</option>
                                        <option value="CE">CE</option>
                                        <option value="PA">PA</option>
                                        <option value="PE">PE</option>
                                        <option value="RC">RC</option>
                                        <option value="TI">TI</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>N°Documento *</label>
                                    <input type="text" name="ndocumento" className="form-control"
                                        value={formData.ndocumento} readOnly />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card card-info card-outline">
                    <div className="card-header"><b>Información General</b></div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Fecha *</label>
                                    <input type="date" name="fecha" className="form-control"
                                        value={formData.fecha} onChange={handleInputChange} required />
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="form-group">
                                    <label>Incapacidad *</label>
                                    <select name="incapacidad" className="form-control" value={formData.incapacidad} onChange={handleInputChange} required>
                                        <option value="NO">NO</option>
                                        <option value="SI">SI</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Tipo de Usuario *</label>
                                    <select name="tipo_usuario" className="form-control" value={formData.tipo_usuario} onChange={handleInputChange} required>
                                        <option value="12">Particular</option>
                                        <option value="01">Contributivo cotizante</option>
                                        <option value="02">Contributivo beneficiario</option>
                                        <option value="03">Contributivo adicional</option>
                                        <option value="04">Subsidiado</option>
                                        <option value="05">No afiliado</option>
                                        <option value="06">Especial o Excepcion cotizante</option>
                                        <option value="07">Especial o Excepcion beneficiario</option>
                                        <option value="08">Personas privadas de la libertad a cargo del Fondo Nacional de Salud</option>
                                        <option value="09">Tomador / Amparado ARL</option>
                                        <option value="10">Tomador / Amparado SOAT</option>
                                        <option value="11">Tomador / Amparado Planes voluntarios de salud</option>
                                        <option value="13">Especial o Exepcion no cotizante Ley 352 de 1997</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-7">
                                <div className="form-group">
                                    <label>Profesional *</label>
                                    <select name="profesionalcode" className="form-control" value={formData.profesionalcode} onChange={handleInputChange} required>
                                        <option value="">Seleccionar...</option>
                                        {profesionales.map(prof => (
                                            <option key={prof.id} value={prof.id}>{prof.name} {prof.lastname}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <hr />
                        <label><b>Selecciona el Tipo Servicio:</b></label><br />
                        <label className={`btn ${formData.tipo_consulta ? 'btn-info' : 'btn-secondary'}`} style={{ marginRight: '8px' }}>
                            <input type="checkbox" name="tipo_consulta" checked={formData.tipo_consulta} onChange={handleInputChange} className="d-none" />
                            <i className="far fa-calendar-check mr-1"></i> Consulta
                        </label>
                        <label className={`btn ${formData.tipo_procedimiento ? 'btn-info' : 'btn-secondary'}`} style={{ marginRight: '8px' }}>
                            <input type="checkbox" name="tipo_procedimiento" checked={formData.tipo_procedimiento} onChange={handleInputChange} className="d-none" />
                            <i className="fas fa-procedures mr-1"></i> Procedimiento
                        </label>
                    </div>
                </div>

                {showConsulta && (
                    <div className="card card-info card-outline">
                        <div className="card-header"><b>Información de la Consulta</b></div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-9">
                                    <div className="form-group">
                                        <label>Código de Consulta *</label>
                                        <div className="input-group">
                                            <input type="text" name="cupsc" className={`form-control ${campoConError('cupsc') ? 'is-invalid' : ''}`} placeholder="Seleccione, Haciendo Click en el Icono" value={formData.cupsc} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={abrirModalConsulta}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Valor Total *</label>
                                        <input type="number" name="valor_consulta" className="form-control" 
                                            value={formData.valor_consulta} 
                                            onChange={handleInputChange}
                                            step="0.01" required />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Principal *</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10c" className={`form-control ${campoConError('cie10c') ? 'is-invalid' : ''}`} placeholder="Seleccione, Haciendo Click en el Icono" value={formData.cie10c} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10c')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Relacionado 1</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10c1" className="form-control" value={formData.cie10c1} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10c1')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Relacionado 2</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10c2" className="form-control" value={formData.cie10c2} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10c2')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Relacionado 3</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10c3" className="form-control" value={formData.cie10c3} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10c3')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Modalidad de Atención *</label>
                                        <select name="modalidad_consulta" className={`form-control ${campoConError('modalidad_consulta') ? 'is-invalid' : ''}`} value={formData.modalidad_consulta} onChange={handleInputChange} required>
                                            <option value="01">INTRAMURAL</option>
                                            <option value="02">EXTRAMURAL UNIDAD MÓVIL</option>
                                            <option value="03">EXTRAMURAL DOMICILIARIA</option>
                                            <option value="04">EXTRAMURAL JORNADA DE SALUD</option>
                                            <option value="06">TELEMEDICINA INTERACTIVA</option>
                                            <option value="07">TELEMEDICINA NO INTERACTIVA</option>
                                            <option value="08">TELEMEDICINA TELEXPERTICIA</option>
                                            <option value="09">TELEMEDICINA TELEMONITOREO</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Grupo de Servicios *</label>
                                        <select name="grupo_servicios" className={`form-control ${campoConError('grupo_servicios') ? 'is-invalid' : ''}`} value={formData.grupo_servicios} onChange={handleInputChange} required>
                                            <option value="01">CONSULTA EXTERNA</option>
                                            <option value="02">APOYO DIAGNÓSTICO Y COMPLEMENTACIÓN TERAPÉUTICA</option>
                                            <option value="03">INTERNACIÓN</option>
                                            <option value="04">QUIRÚRGICO</option>
                                            <option value="05">ATENCIÓN INMEDIATA</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <SearchableSelect
                                        name="servicios"
                                        value={formData.servicios}
                                        onChange={handleInputChange}
                                        options={servicios}
                                        label="Servicios"
                                        placeholder="Seleccione un servicio..."
                                        required={true}
                                        isInvalid={campoConError('servicios')}
                                        helpText="Busque por código o nombre del servicio"
                                    />
                                </div>
                                <div className="col-md-12">
                                    <SearchableSelect
                                        name="finalidadconsulta"
                                        value={formData.finalidadconsulta}
                                        onChange={handleInputChange}
                                        options={finalidadConsulta}
                                        label="Finalidad Consulta"
                                        placeholder="Seleccione la finalidad..."
                                        required={true}
                                        isInvalid={campoConError('finalidadconsulta')}
                                        helpText="Busque por código o nombre"
                                    />
                                </div>
                                <div className="col-md-12">
                                    <SearchableSelect
                                        name="causaexternaconsulta"
                                        value={formData.causaexternaconsulta}
                                        onChange={handleInputChange}
                                        options={causaExterna}
                                        label="Causa Externa Consulta"
                                        placeholder="Seleccione la causa externa..."
                                        required={true}
                                        isInvalid={campoConError('causaexternaconsulta')}
                                        helpText="Busque por código o nombre"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Tipo de Diagnóstico *</label>
                                        <select name="tipo_diagnostico" className="form-control" required>
                                            <option value="01">IMPRESIÓN DIAGNÓSTICA</option>
                                            <option value="02">CONFIRMADO NUEVO</option>
                                            <option value="03">CONFIRMADO REPETIDO</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showProcedimiento && (
                    <div className="card card-info card-outline">
                        <div className="card-header"><b>Información del Procedimiento</b></div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-9">
                                    <div className="form-group">
                                        <label>Código de Procedimiento *</label>
                                        <div className="input-group">
                                            <input type="text" name="cupsp" className={`form-control ${campoConError('cupsp') ? 'is-invalid' : ''}`} placeholder="Seleccione, Haciendo Click en el Icono" value={formData.cupsp} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={abrirModalProcedimiento}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Valor Total *</label>
                                        <input type="number" name="valor_procedimiento" className="form-control" 
                                            value={formData.valor_procedimiento} 
                                            onChange={handleInputChange}
                                            step="0.01" required />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Procedimiento *</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10p" className={`form-control ${campoConError('cie10p') ? 'is-invalid' : ''}`} placeholder="Seleccione, Haciendo Click en el Icono" value={formData.cie10p} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10p')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Relacionado 1</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10p1" className="form-control" value={formData.cie10p1} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10p1')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>Diagnóstico Complicación</label>
                                        <div className="input-group">
                                            <input type="text" name="cie10pc" className="form-control" value={formData.cie10pc} readOnly />
                                            <div className="input-group-append">
                                                <button type="button" className="btn btn-info" onClick={() => abrirModalCIE10('cie10pc')}>
                                                    <i className="fas fa-search"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Modalidad de Atención *</label>
                                        <select name="modalidad_procedimiento" className={`form-control ${campoConError('modalidad_procedimiento') ? 'is-invalid' : ''}`} value={formData.modalidad_procedimiento} onChange={handleInputChange} required>
                                            <option value="01">INTRAMURAL</option>
                                            <option value="02">EXTRAMURAL UNIDAD MÓVIL</option>
                                            <option value="03">EXTRAMURAL DOMICILIARIA</option>
                                            <option value="04">EXTRAMURAL JORNADA DE SALUD</option>
                                            <option value="06">TELEMEDICINA INTERACTIVA</option>
                                            <option value="07">TELEMEDICINA NO INTERACTIVA</option>
                                            <option value="08">TELEMEDICINA TELEXPERTICIA</option>
                                            <option value="09">TELEMEDICINA TELEMONITOREO</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Grupo de Servicios *</label>
                                        <select name="grupo_servicios_procedimientos" className={`form-control ${campoConError('grupo_servicios_procedimientos') ? 'is-invalid' : ''}`} value={formData.grupo_servicios_procedimientos} onChange={handleInputChange} required>
                                            <option value="01">CONSULTA EXTERNA</option>
                                            <option value="02">APOYO DIAGNÓSTICO Y COMPLEMENTACIÓN TERAPÉUTICA</option>
                                            <option value="03">INTERNACIÓN</option>
                                            <option value="04">QUIRÚRGICO</option>
                                            <option value="05">ATENCIÓN INMEDIATA</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <SearchableSelect
                                        name="servicios_procedimientos"
                                        value={formData.servicios_procedimientos}
                                        onChange={handleInputChange}
                                        options={servicios}
                                        label="Servicios"
                                        placeholder="Seleccione un servicio..."
                                        required={true}
                                        isInvalid={campoConError('servicios_procedimientos')}
                                        helpText="Busque por código o nombre del servicio"
                                    />
                                </div>
                                <div className="col-md-12">
                                    <SearchableSelect
                                        name="finalidadprocedimiento"
                                        value={formData.finalidadprocedimiento}
                                        onChange={handleInputChange}
                                        options={finalidadProcedimiento}
                                        label="Finalidad Procedimiento"
                                        placeholder="Seleccione la finalidad..."
                                        required={true}
                                        isInvalid={campoConError('finalidadprocedimiento')}
                                        helpText="Busque por código o nombre"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label>Concepto del Recaudo</label>
                                        <select name="conceptoRecaudo" className="form-control" required>
                                            <option value="05">NO APLICA</option>
                                            <option value="01">COPAGO</option>
                                            <option value="02">CUOTA MODERADORA</option>
                                            <option value="03">PAGOS COMPARTIDOS EN PLANES VOLUNTARIOS DE SALUD</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label>Valor Cuota Moderadora</label>
                                        <input type="number" name="valorpagomoderador" className="form-control" placeholder="0" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}



                <div className="row">
                    <div className="col-md-12">
                        <button type="submit" className="btn btn-info">
                            <i className="fas fa-save"></i> Guardar
                        </button>
                    </div>
                </div>
            </form>

            {/* Modal Consultas — portal para no recortar el buscador dentro de Modal padre */}
            {showModalConsulta &&
                createPortal(
                    <>
                        <style>{CUPS_RIPS_MODAL_CHIP_CSS}</style>
                        <div className="modal fade show cups-rips-modal-portal" style={{ display: 'block', zIndex: 20000 }} tabIndex="-1">
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Seleccionar Código de Consulta</h5>
                                    <button type="button" className="close" onClick={() => setShowModalConsulta(false)}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="form-group mb-3">
                                        <label className="small text-muted mb-1">Clasificación</label>
                                        <div className="chip-row mb-2">
                                            <button
                                                type="button"
                                                className={`chip-filter ${clasificacionFiltroCupsModal === '' ? 'active' : ''}`}
                                                onClick={() => setClasificacionFiltroCupsModal('')}
                                            >
                                                Todas
                                            </button>
                                            {clasificacionesConsultaModal.map((clas) => (
                                                <button
                                                    key={clas}
                                                    type="button"
                                                    className={`chip-filter ${clasificacionFiltroCupsModal === clas ? 'active' : ''}`}
                                                    onClick={() => setClasificacionFiltroCupsModal(clas)}
                                                >
                                                    {clas}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label htmlFor="filtro-cups-consulta-convenio" className="small text-muted mb-1">Buscar CUPS o nombre</label>
                                        <input
                                            id="filtro-cups-consulta-convenio"
                                            type="text"
                                            className="form-control form-control-lg"
                                            placeholder="Filtrar lista…"
                                            value={filtroCupsModal}
                                            onChange={(e) => setFiltroCupsModal(e.target.value)}
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="table-responsive" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                                        <table className="table table-bordered table-hover table-sm mb-0">
                                            <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Nombre</th>
                                                    <th>Precio</th>
                                                    <th>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {consultasCupsFiltradas.map((item, index) => (
                                                    <tr key={item.id != null ? `lc-${item.id}` : `lc-${index}`}>
                                                        <td>{item.codigorips}</td>
                                                        <td>{item.nombre}</td>
                                                        <td>${parseFloat(item.precio || 0).toLocaleString('es-CO', {minimumFractionDigits: 2})}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => seleccionarConsulta(item)}
                                                            >
                                                                Seleccionar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {consultasCupsFiltradas.length === 0 && (
                                        <p className="text-muted small mb-0 mt-2">No hay resultados para la búsqueda.</p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModalConsulta(false)}>
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    </>,
                    document.body
                )}

            {/* Modal Procedimientos */}
            {showModalProcedimiento &&
                createPortal(
                    <>
                        <style>{CUPS_RIPS_MODAL_CHIP_CSS}</style>
                        <div className="modal fade show cups-rips-modal-portal" style={{ display: 'block', zIndex: 20000 }} tabIndex="-1">
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Seleccionar Código de Procedimiento</h5>
                                    <button type="button" className="close" onClick={() => setShowModalProcedimiento(false)}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="form-group mb-3">
                                        <label className="small text-muted mb-1">Clasificación</label>
                                        <div className="chip-row mb-2">
                                            <button
                                                type="button"
                                                className={`chip-filter ${clasificacionFiltroCupsModal === '' ? 'active' : ''}`}
                                                onClick={() => setClasificacionFiltroCupsModal('')}
                                            >
                                                Todas
                                            </button>
                                            {clasificacionesProcedimientoModal.map((clas) => (
                                                <button
                                                    key={clas}
                                                    type="button"
                                                    className={`chip-filter ${clasificacionFiltroCupsModal === clas ? 'active' : ''}`}
                                                    onClick={() => setClasificacionFiltroCupsModal(clas)}
                                                >
                                                    {clas}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label htmlFor="filtro-cups-procedimiento-convenio" className="small text-muted mb-1">Buscar CUPS o nombre</label>
                                        <input
                                            id="filtro-cups-procedimiento-convenio"
                                            type="text"
                                            className="form-control form-control-lg"
                                            placeholder="Filtrar lista…"
                                            value={filtroCupsModal}
                                            onChange={(e) => setFiltroCupsModal(e.target.value)}
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="table-responsive" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                                        <table className="table table-bordered table-hover table-sm mb-0">
                                            <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Nombre</th>
                                                    <th>Precio</th>
                                                    <th>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {procedimientosCupsFiltrados.map((item, index) => (
                                                    <tr key={item.id != null ? `lp-${item.id}` : `lp-${index}`}>
                                                        <td>{item.codigorips}</td>
                                                        <td>{item.nombre}</td>
                                                        <td>${parseFloat(item.precio || 0).toLocaleString('es-CO', {minimumFractionDigits: 2})}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => seleccionarProcedimiento(item)}
                                                            >
                                                                Seleccionar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {procedimientosCupsFiltrados.length === 0 && (
                                        <p className="text-muted small mb-0 mt-2">No hay resultados para la búsqueda.</p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModalProcedimiento(false)}>
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    </>,
                    document.body
                )}

            {/* Modal CIE10 */}
            <ModalCIE10
                show={showModalCIE10}
                onClose={() => setShowModalCIE10(false)}
                onSelect={seleccionarCIE10}
                title="Seleccionar Código CIE10 - Diagnóstico"
            />

        </div>
    );
}

export default RegistroRipsConvenio;